import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Shield, CheckCircle2, AlertTriangle, XCircle, Download,
  Mail, Loader2, Clock, ArrowLeft, Lock, Globe, Scan,
  Zap, Cookie, RefreshCw
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// ── useWindowWidth ───────────────────────────────────────────────────
function useWindowWidth() {
  const [w, setW] = useState(window.innerWidth);
  useEffect(() => {
    const fn = () => setW(window.innerWidth);
    window.addEventListener('resize', fn);
    return () => window.removeEventListener('resize', fn);
  }, []);
  return w;
}

// ── Color helpers ────────────────────────────────────────────────────
const scoreColor  = (s) => s >= 80 ? '#00FF94' : s >= 60 ? '#FFD600' : '#FF0055';
const statusColor = (s) => {
  const v = String(s).toLowerCase();
  if (['success','excellent','good'].includes(v)) return '#00FF94';
  if (['warning','average'].includes(v))          return '#FFD600';
  return '#FF0055';
};
const scoreRating = (s) =>
  s >= 90 ? 'Excellent Security Posture'       :
  s >= 80 ? 'Good — Minor Improvements Needed' :
  s >= 60 ? 'Average — Several Issues Found'   :
  s >= 40 ? 'Poor — Immediate Action Required' :
            'Critical — Urgent Action Required';
const letterGrade = (s) =>
  s >= 90 ? 'A+' : s >= 80 ? 'A' : s >= 70 ? 'B' :
  s >= 60 ? 'C'  : s >= 40 ? 'D' : 'F';

// ── Score Ring ───────────────────────────────────────────────────────
function ScoreRing({ score, size = 130 }) {
  const c = scoreColor(score);
  const r = (size / 2) - 10;
  const circ = 2 * Math.PI * r;
  const dash = (score / 100) * circ;
  const cx = size / 2, cy = size / 2;
  return (
    <div style={{ position: 'relative', width: size, height: size, flexShrink: 0 }}>
      <svg width={size} height={size} style={{ transform: 'rotate(-90deg)' }}>
        <circle cx={cx} cy={cy} r={r} fill="none" stroke="#1a1a3e" strokeWidth="10" />
        <circle cx={cx} cy={cy} r={r} fill="none" stroke={c} strokeWidth="10"
          strokeDasharray={`${dash} ${circ - dash}`}
          style={{ transition: 'stroke-dasharray 1.2s ease' }} />
      </svg>
      <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
        <span style={{ fontSize: size > 100 ? 34 : 26, fontWeight: 800, color: c, lineHeight: 1 }}>{score}</span>
        <span style={{ fontSize: 11, color: '#b0b8d0' }}>/100</span>
      </div>
    </div>
  );
}

function StatusIcon({ status }) {
  const v = String(status).toLowerCase();
  if (['success','excellent','good'].includes(v)) return <CheckCircle2 size={18} color="#00FF94" />;
  if (['warning','average'].includes(v))          return <AlertTriangle size={18} color="#FFD600" />;
  return <XCircle size={18} color="#FF0055" />;
}

// ── Protocol Card ────────────────────────────────────────────────────
function ProtocolCard({ icon, title, data, delay = 0 }) {
  if (!data) return null;
  const { status, score, message } = data;
  const sc = statusColor(status);
  const skip = new Set(['status','message','score']);
  const extras = Object.entries(data).filter(([k]) => !skip.has(k));

  return (
    <motion.div
      initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }}
      transition={{ delay, duration: 0.4 }}
      style={{
        background: '#0d0d2b', borderRadius: 12,
        border: `1px solid ${sc}`, borderTop: `3px solid ${sc}`,
        padding: '18px 18px', display: 'flex', flexDirection: 'column', gap: 10,
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ color: '#00F0FF' }}>{icon}</span>
          <span style={{ fontWeight: 700, fontSize: 13, color: '#fff', fontFamily: 'var(--font-heading)' }}>{title}</span>
        </div>
        <StatusIcon status={status} />
      </div>

      <div>
        <span style={{
          background: sc + '22', border: `1px solid ${sc}`, borderRadius: 20,
          padding: '2px 10px', fontSize: 10, fontWeight: 700, color: sc,
        }}>
          {String(status).toUpperCase()}
        </span>
      </div>

      <p style={{ fontSize: 12, color: '#b0b8d0', margin: 0, lineHeight: 1.5 }}>{message}</p>

      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <div style={{ flex: 1, height: 5, background: '#1a1a3e', borderRadius: 3, overflow: 'hidden' }}>
          <div style={{
            width: `${Math.min((score / 15) * 100, 100)}%`, height: '100%',
            background: sc, borderRadius: 3, transition: 'width 1s ease',
          }} />
        </div>
        <span style={{ fontSize: 11, color: sc, fontWeight: 700, minWidth: 40 }}>{score} pts</span>
      </div>

      {extras.slice(0, 3).map(([k, v]) => {
        const display = Array.isArray(v) ? v.join(', ') : String(v);
        if (!display || display === 'false' || display === '{}') return null;
        return (
          <div key={k} style={{ fontSize: 11, color: '#4a4a6a', display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            <span style={{ color: '#7000FF', fontWeight: 600 }}>{k}:</span>
            <span style={{ color: '#6a6a8a', wordBreak: 'break-all' }}>
              {display.length > 70 ? display.slice(0, 67) + '…' : display}
            </span>
          </div>
        );
      })}
    </motion.div>
  );
}

const SCAN_STEPS = [
  'Checking SSL/HTTPS Certificate…',
  'Analysing Security Headers…',
  'Scanning Open Ports…',
  'Validating DNS Configuration…',
  'Detecting Technology Stack…',
  'Checking Malware / Blacklist Status…',
  'Measuring Response Performance…',
  'Auditing Cookie Security…',
];

const PROTOCOLS = [
  { key: 'ssl_check',        icon: <Lock size={15}         />, title: 'SSL / HTTPS'        },
  { key: 'security_headers', icon: <Shield size={15}       />, title: 'Sec Headers'        },
  { key: 'port_scan',        icon: <Scan size={15}         />, title: 'Open Ports'         },
  { key: 'dns_config',       icon: <Globe size={15}        />, title: 'DNS Config'         },
  { key: 'technology_stack', icon: <Zap size={15}          />, title: 'Tech Stack'         },
  { key: 'blacklist_check',  icon: <AlertTriangle size={15}/>, title: 'Blacklist'          },
  { key: 'response_time',    icon: <Clock size={15}        />, title: 'Performance'        },
  { key: 'cookie_security',  icon: <Cookie size={15}       />, title: 'Cookies'            },
];

const PROTOCOLS_FULL = [
  { key: 'ssl_check',        icon: <Lock size={16}         />, title: 'SSL / HTTPS Certificate'   },
  { key: 'security_headers', icon: <Shield size={16}       />, title: 'HTTP Security Headers'     },
  { key: 'port_scan',        icon: <Scan size={16}         />, title: 'Open Port Scan'            },
  { key: 'dns_config',       icon: <Globe size={16}        />, title: 'DNS Configuration'         },
  { key: 'technology_stack', icon: <Zap size={16}          />, title: 'Technology Stack'          },
  { key: 'blacklist_check',  icon: <AlertTriangle size={16}/>, title: 'Malware / Blacklist Check' },
  { key: 'response_time',    icon: <Clock size={16}        />, title: 'Response Performance'      },
  { key: 'cookie_security',  icon: <Cookie size={16}       />, title: 'Cookie Security'           },
];

// ── Main Component ───────────────────────────────────────────────────
const ResultsPage = () => {
  const { scanId } = useParams();
  const navigate   = useNavigate();
  const width      = useWindowWidth();
  const isMobile   = width < 640;
  const isTablet   = width < 1024;

  const [scan, setScan]               = useState(null);
  const [loading, setLoading]         = useState(true);
  const [downloading, setDownloading] = useState(false);
  const [emailing, setEmailing]       = useState(false);
  const [progress, setProgress]       = useState(0);
  const pollRef = useRef(null);

  useEffect(() => {
    const fetchScan = async () => {
      try {
        const res  = await axios.get(`${API}/scan/${scanId}`);
        const data = res.data;
        setScan(data);
        setLoading(false);
        if (data.status === 'scanning')   setProgress(p => Math.min(p + 10, 88));
        if (data.status === 'completed') { setProgress(100); clearInterval(pollRef.current); }
        if (data.status === 'failed')     clearInterval(pollRef.current);
      } catch { setLoading(false); }
    };
    fetchScan();
    pollRef.current = setInterval(fetchScan, 3500);
    return () => clearInterval(pollRef.current);
  }, [scanId]);

  const downloadReport = async () => {
    setDownloading(true);
    try {
      const res = await axios.get(`${API}/scan/${scanId}/report`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a   = document.createElement('a');
      a.href = url; a.download = `webshield_report_${scanId}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      toast.success('PDF report downloaded!');
    } catch { toast.error('Failed to download report'); }
    finally  { setDownloading(false); }
  };

  const resendEmail = async () => {
    setEmailing(true);
    try {
      await axios.post(`${API}/scan/${scanId}/resend-email`);
      toast.success('Report re-sent to ' + scan.email);
    } catch { toast.error('Failed to resend email'); }
    finally  { setEmailing(false); }
  };

  if (loading) return (
    <div style={{ minHeight: '100vh', background: '#020210', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ textAlign: 'center' }}>
        <Loader2 size={48} color="#00F0FF" style={{ animation: 'spin 1s linear infinite', marginBottom: 16 }} />
        <p style={{ color: '#b0b8d0' }}>Loading scan results…</p>
      </div>
    </div>
  );

  if (!scan) return (
    <div style={{ minHeight: '100vh', background: '#020210', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
      <div style={{ textAlign: 'center' }}>
        <XCircle size={48} color="#FF0055" style={{ marginBottom: 16 }} />
        <p style={{ color: '#b0b8d0', marginBottom: 20 }}>Scan not found</p>
        <button onClick={() => navigate('/scanner')} className="btn-primary">Start New Scan</button>
      </div>
    </div>
  );

  const isScanning  = ['scanning','pending','awaiting_payment'].includes(scan.status);
  const isCompleted = scan.status === 'completed';
  const isFailed    = scan.status === 'failed';

  return (
    <div className="min-h-screen bg-background" style={{ paddingBottom: 60 }}>

      {/* ── Sticky header ── */}
      <div style={{
        position: 'sticky', top: 0, zIndex: 40,
        background: 'rgba(2,2,16,0.9)', backdropFilter: 'blur(12px)',
        borderBottom: '1px solid rgba(112,0,255,0.3)',
        padding: isMobile ? '12px 16px' : isTablet ? '12px 20px' : '14px 32px',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 10 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <button onClick={() => navigate('/')} style={{
              background: 'transparent', border: 'none', color: '#b0b8d0',
              display: 'flex', alignItems: 'center', gap: 5, cursor: 'pointer', fontSize: 13, padding: 0,
            }}>
              <ArrowLeft size={16} /> {isMobile ? '' : 'Home'}
            </button>
            {!isMobile && <span style={{ color: '#2a2a4a' }}>|</span>}
            <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
              <Shield size={18} color="#00F0FF" />
              <span style={{ fontWeight: 700, fontSize: isMobile ? 14 : 15, color: '#fff', fontFamily: 'var(--font-heading)' }}>
                Security Results
              </span>
            </div>
          </div>

          {isCompleted && (
            <div style={{ display: 'flex', gap: 8 }}>
              <button onClick={downloadReport} disabled={downloading} className="btn-primary"
                style={{ fontSize: 12, padding: isMobile ? '7px 12px' : '8px 16px', display: 'flex', alignItems: 'center', gap: 6 }}>
                {downloading ? <Loader2 size={13} className="animate-spin" /> : <Download size={13} />}
                {isMobile ? 'PDF' : 'Download PDF'}
              </button>
              {scan.email && (
                <button onClick={resendEmail} disabled={emailing} style={{
                  background: 'transparent', border: '1px solid #7000FF', borderRadius: 8,
                  padding: isMobile ? '7px 12px' : '8px 16px', color: '#b0b8d0',
                  fontSize: 12, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6,
                }}>
                  {emailing ? <Loader2 size={13} className="animate-spin" /> : <Mail size={13} />}
                  {isMobile ? 'Email' : 'Resend Email'}
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      <div style={{ maxWidth: 1060, margin: '0 auto', padding: isMobile ? '20px 14px 0' : isTablet ? '28px 20px 0' : '36px 24px 0' }}>

        {/* Target URL */}
        <div style={{ marginBottom: 22 }}>
          <p style={{ color: '#95959e', fontSize: 11, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 3 }}>Target</p>
          <p style={{ color: '#00F0FF', fontWeight: 600, fontSize: isMobile ? 13 : 15, wordBreak: 'break-all', margin: 0 }} className="font-mono">
            {scan.url}
          </p>
        </div>

        {/* ── SCANNING STATE ── */}
        <AnimatePresence>
          {isScanning && (
            <motion.div
              initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
              className="glass-card"
              style={{ padding: isMobile ? '36px 20px' : isTablet ? '44px 28px' : '56px 40px', textAlign: 'center', marginBottom: 24 }}
            >
              <div style={{ position: 'relative', width: isMobile ? 90 : isTablet ? 110 : 120, height: isMobile ? 90 : isTablet ? 110 : 120, margin: '0 auto 24px' }}>
                <div style={{ position: 'absolute', inset: 0, border: '4px solid rgba(0,240,255,0.15)', borderRadius: '50%' }} />
                <div style={{ position: 'absolute', inset: 0, border: '4px solid transparent', borderTopColor: '#00F0FF', borderRadius: '50%', animation: 'spin 1s linear infinite' }} />
                <Shield size={isMobile ? 32 : isTablet ? 38 : 42} color="#00F0FF" style={{ position: 'absolute', inset: 0, margin: 'auto' }} />
              </div>

              <h2 className="font-heading" style={{ fontSize: isMobile ? 22 : 26, fontWeight: 700, marginBottom: 8, color: '#fff' }}>
                Scanning in Progress
              </h2>
              <p style={{ color: '#b0b8d0', marginBottom: 24, fontSize: isMobile ? 13 : 15 }}>
                Running 8 comprehensive security checks…
              </p>

              <div style={{ maxWidth: 440, margin: '0 auto 20px', background: '#1a1a3e', borderRadius: 6, height: 8, overflow: 'hidden' }}>
                <div style={{
                  height: '100%', width: `${progress}%`,
                  background: 'linear-gradient(90deg,#7000FF,#00F0FF)',
                  transition: 'width 0.8s ease', borderRadius: 6,
                }} />
              </div>
              <p style={{ color: '#7000FF', fontWeight: 600, fontSize: 13, marginBottom: 24 }}>{progress}% complete</p>

              <div style={{ maxWidth: 360, margin: '0 auto', textAlign: 'left' }}>
                {SCAN_STEPS.map((text, i) => {
                  const done = progress > (i + 1) * 11;
                  return (
                    <motion.div
                      key={i}
                      initial={{ opacity: 0, x: -16 }} animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: i * 0.25 }}
                      style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 9 }}
                    >
                      {done ? <CheckCircle2 size={14} color="#00FF94" /> : <Clock size={14} color="#7000FF" style={{ opacity: 0.6 }} />}
                      <span style={{ fontSize: isMobile ? 11 : 12, color: done ? '#00FF94' : '#b0b8d0', fontFamily: 'monospace' }}>
                        {text}
                      </span>
                    </motion.div>
                  );
                })}
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* ── FAILED STATE ── */}
        {isFailed && (
          <div className="glass-card" style={{ padding: isMobile ? '40px 20px' : '56px 40px', textAlign: 'center', borderTop: '3px solid #FF0055' }}>
            <XCircle size={48} color="#FF0055" style={{ margin: '0 auto 14px' }} />
            <h2 className="font-heading" style={{ fontSize: isMobile ? 20 : 24, fontWeight: 700, marginBottom: 8, color: '#fff' }}>
              Scan Failed
            </h2>
            <p style={{ color: '#b0b8d0', marginBottom: 24 }}>An error occurred during the security scan.</p>
            <button onClick={() => navigate('/scanner')} className="btn-primary">Try Again</button>
          </div>
        )}

        {/* ── COMPLETED STATE ── */}
        {isCompleted && scan.results && (
          <div>

            {/* Score card */}
            <motion.div
              initial={{ opacity: 0, scale: 0.97 }} animate={{ opacity: 1, scale: 1 }}
              className="glass-card"
              style={{
                padding: isMobile ? '22px 18px' : isTablet ? '26px 24px' : '32px 36px', marginBottom: 18,
                borderTop: `3px solid ${scoreColor(scan.results.overall_score)}`,
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: isMobile ? 20 : 36, flexWrap: isMobile ? 'nowrap' : 'wrap' }}>
                <ScoreRing score={scan.results.overall_score} size={isMobile ? 96 : isTablet ? 115 : 130} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{
                    display: 'flex', alignItems: isMobile ? 'flex-start' : 'center',
                    gap: 10, flexWrap: 'wrap', marginBottom: 8,
                    flexDirection: isMobile ? 'column' : 'row',
                  }}>
                    <span style={{
                      fontSize: isMobile ? 16 : 20, fontWeight: 800,
                      color: scoreColor(scan.results.overall_score),
                      fontFamily: 'var(--font-heading)', lineHeight: 1.2,
                    }}>
                      {scoreRating(scan.results.overall_score)}
                    </span>
                    <span style={{
                      background: scoreColor(scan.results.overall_score) + '22',
                      border: `1px solid ${scoreColor(scan.results.overall_score)}`,
                      borderRadius: 8, padding: isMobile ? '3px 10px' : '4px 14px',
                      fontSize: isMobile ? 15 : 18, fontWeight: 800,
                      color: scoreColor(scan.results.overall_score),
                      fontFamily: 'var(--font-heading)',
                      display: 'inline-block',
                    }}>
                      Grade: {letterGrade(scan.results.overall_score)}
                    </span>
                  </div>

                  {scan.results.scan_timestamp && (
                    <p style={{ color: '#bdbdc5', fontSize: 11, marginBottom: 16 }}>
                      Scanned {new Date(scan.results.scan_timestamp).toLocaleString()}
                    </p>
                  )}

                  <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                    <button onClick={downloadReport} disabled={downloading} className="btn-primary"
                      data-testid="download-report-button"
                      style={{ fontSize: isMobile ? 13 : 14, padding: isMobile ? '9px 16px' : '10px 22px', display: 'flex', alignItems: 'center', gap: 7 }}>
                      {downloading ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />}
                      {isMobile ? 'Download PDF' : 'Download PDF Report'}
                    </button>
                    {scan.email && (
                      <button onClick={resendEmail} disabled={emailing} style={{
                        background: 'transparent', border: '1px solid #1a1a3e', borderRadius: 8,
                        padding: isMobile ? '9px 14px' : '10px 20px',
                        color: '#b0b8d0', fontSize: isMobile ? 13 : 14,
                        display: 'flex', alignItems: 'center', gap: 7, cursor: 'pointer',
                      }}>
                        {emailing ? <Loader2 size={14} className="animate-spin" /> : <Mail size={14} />}
                        Resend Email
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Mini score grid — 4 col desktop, 2x4 mobile */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: isMobile ? 'repeat(2,1fr)' : isTablet ? 'repeat(4,1fr)' : 'repeat(8,1fr)',
              gap: isMobile ? 8 : 10, marginBottom: 24,
            }}>
              {PROTOCOLS.map((p) => {
                const d  = scan.results[p.key] || {};
                const sc = statusColor(d.status || 'error');
                return (
                  <div key={p.key} style={{
                    background: '#0d0d2b', border: `1px solid ${sc}`,
                    borderRadius: 8, padding: isMobile ? '12px 8px' : '12px 10px', textAlign: 'center',
                  }}>
                    <div style={{ color: '#00F0FF', marginBottom: 4, display: 'flex', justifyContent: 'center' }}>{p.icon}</div>
                    <div style={{ fontSize: isMobile ? 9 : 10, color: '#fff', fontWeight: 700, marginBottom: 4, lineHeight: 1.3 }}>
                      {p.title}
                    </div>
                    <div style={{ color: sc, fontSize: isMobile ? 12 : 11, fontWeight: 700 }}>{d.score ?? 0} pts</div>
                  </div>
                );
              })}
            </div>

            {/* Protocol detail cards */}
            <h3 style={{ color: '#00F0FF', fontWeight: 700, fontSize: isMobile ? 14 : 16, marginBottom: 14, fontFamily: 'var(--font-heading)' }}>
              Detailed Protocol Analysis
            </h3>
            <div style={{
              display: 'grid',
              gridTemplateColumns: isMobile ? '1fr' : 'repeat(2,1fr)',
              gap: isMobile ? 12 : isTablet ? 14 : 14, marginBottom: 28,
            }}>
              {PROTOCOLS_FULL.map((p, i) => (
                <ProtocolCard key={p.key} icon={p.icon} title={p.title}
                  data={scan.results[p.key]} delay={isMobile ? 0 : i * 0.07} />
              ))}
            </div>

            {/* Scan again */}
            <div style={{ textAlign: 'center' }}>
              <button onClick={() => navigate('/scanner')} style={{
                background: 'transparent', border: '1px solid #2a2a4a',
                borderRadius: 10, padding: isMobile ? '11px 24px' : '12px 32px',
                color: '#b0b8d0', fontSize: 14, cursor: 'pointer',
                display: 'inline-flex', alignItems: 'center', gap: 8,
                width: isMobile ? '100%' : 'auto', justifyContent: 'center',
              }}>
                <RefreshCw size={15} /> Scan Another Website
              </button>
            </div>
          </div>
        )}
      </div>

      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
        .animate-spin { animation: spin 1s linear infinite; }
      `}</style>
    </div>
  );
};

export default ResultsPage;